import FiatCryptoBridgeArtifactDev from '../contracts-dev/FiatCryptoBridge.json';
import contractAddressDev from '../contracts-dev/contract-address.json';

import FiatCryptoBridgeArtifactSepolia from '../contracts/FiatCryptoBridge.json';
import contractAddressSepolia from '../contracts/contract-address.json';

import FiatCryptoBridgeArtifactMainnet from '../contracts-mainnet/FiatCryptoBridge.json';
import contractAddressMainnet from '../contracts-mainnet/contract-address.json';

// --- helpers -----------------------------------------------------------------

const CHAIN_IDS = {
  localhost: 31337,
  sepolia: 11155111,
  mainnet: 1,
};

function normalizeChainId(value) {
  if (value == null) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'bigint') {
    return Number(value);
  }

  if (typeof value === 'string') {
    const n = value.startsWith('0x') ? Number.parseInt(value, 16) : Number(value);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

function envTarget() {
  return import.meta.env.VITE_RPC_TARGET || 'localhost';
}

function defaultChainIdFromEnv() {
  const target = envTarget();
  return CHAIN_IDS[target] ?? CHAIN_IDS.localhost;
}

/**
 * Important:
 * Prefer VITE_CHAIN_ID / VITE_RPC_TARGET as the app target.
 *
 * Do not blindly use window.ethereum.chainId as the main source, because if the
 * user is on the wrong wallet network, the app could accidentally load the wrong
 * contract address.
 */
function currentChainId() {
  const fromEnv = normalizeChainId(import.meta.env.VITE_CHAIN_ID);

  if (fromEnv) {
    return fromEnv;
  }

  return defaultChainIdFromEnv();
}

function resolveBundle(chainId) {
  const id = normalizeChainId(chainId) ?? defaultChainIdFromEnv();

  if (id === CHAIN_IDS.localhost) {
    return {
      artifact: FiatCryptoBridgeArtifactDev,
      addresses: contractAddressDev,
      network: 'localhost',
      chainId: CHAIN_IDS.localhost,
    };
  }

  if (id === CHAIN_IDS.sepolia) {
    return {
      artifact: FiatCryptoBridgeArtifactSepolia,
      addresses: contractAddressSepolia,
      network: 'sepolia',
      chainId: CHAIN_IDS.sepolia,
    };
  }

  if (id === CHAIN_IDS.mainnet) {
    return {
      artifact: FiatCryptoBridgeArtifactMainnet,
      addresses: contractAddressMainnet,
      network: 'mainnet',
      chainId: CHAIN_IDS.mainnet,
    };
  }

  throw new Error(`Unsupported chainId: ${id}`);
}

function resolveAddresses(chainId) {
  const bundle = resolveBundle(chainId);

  return {
    FiatCryptoBridge: bundle.addresses.FiatCryptoBridge,
    usdt: bundle.addresses.usdt,
  };
}

function resolveArtifact(chainId = currentChainId()) {
  return resolveBundle(chainId).artifact;
}

// --- exports -----------------------------------------------------------------

export const FiatCryptoBridgeAbi = resolveArtifact().abi;

export const contractAddresses = new Proxy(
  {},
  {
    get(_target, prop) {
      const id = currentChainId();
      return resolveAddresses(id)[prop];
    },

    ownKeys() {
      const id = currentChainId();
      return Reflect.ownKeys(resolveAddresses(id));
    },

    getOwnPropertyDescriptor(_target, key) {
      const id = currentChainId();
      const value = resolveAddresses(id)[key];

      return {
        enumerable: true,
        configurable: true,
        writable: false,
        value,
      };
    },
  }
);

export function getActiveAddresses(chainId = currentChainId()) {
  return resolveAddresses(chainId);
}

export function getActiveChainId() {
  return currentChainId();
}

export function getActiveNetworkName(chainId = currentChainId()) {
  return resolveBundle(chainId).network;
}

export function getActiveContractArtifact(chainId = currentChainId()) {
  return resolveArtifact(chainId);
}
